import { queryOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import type { DashboardResponse, DashboardTab } from '@/features/Dashboard/types';
import { http } from '@/services/http';

import { getDashboardApiUrl, getDashboardShareUrl } from './dashboardUrl';
import { dashboardQueryKeys, normalizeDashboardEntry } from './utils';

const normalizeDashboardResponse = (
  data: Record<string, DashboardResponse>
): Record<string, DashboardTab> =>
  Object.fromEntries(
    Object.entries(data).map(([name, value]) => [name, normalizeDashboardEntry(name, value)])
  );

export const dashboardOptions = {
  all: () =>
    queryOptions({
      queryKey: dashboardQueryKeys.all,
      queryFn: async () => {
        try {
          const { data } =
            await http.internal.get<Record<string, DashboardResponse>>(getDashboardApiUrl());
          return normalizeDashboardResponse(data);
        } catch (error) {
          if (error instanceof AxiosError && error.response?.status === 404) {
            return {};
          }
          throw error;
        }
      },
      retry: (failureCount, error) => {
        if (error instanceof AxiosError && error.response?.status === 404) {
          return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      structuralSharing: false
    }),

  shared: (shareToken: string | null) =>
    queryOptions({
      queryKey: dashboardQueryKeys.shared(shareToken ?? ''),
      queryFn: async () => {
        const { data } = await http.internal.get<Record<string, DashboardResponse>>(
          getDashboardShareUrl(),
          {
            params: { shareToken }
          }
        );
        return normalizeDashboardResponse(data);
      },
      enabled: !!shareToken,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      structuralSharing: false
    })
};
